import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {OverlayRef} from '@angular/cdk/overlay';
import {MatDrawerToggleResult} from '@angular/material/sidenav';
import {finalize, firstValueFrom, forkJoin, Observable, Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, takeUntil} from 'rxjs/operators';
import {TranslocoPipe, TranslocoService} from "@ngneat/transloco";
import {ToastrService} from "ngx-toastr";
import {YearlyPlansPageComponent} from "../absences-leave-management.component";
import {Absence, AbsencesLeaveService} from "../absences-leave.service";
import {Utils} from "../../../../shared/utils";
import {FuseConfirmationService} from "../../../../../@fuse/services/confirmation";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {DatePipe, LowerCasePipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatTooltip} from "@angular/material/tooltip";
import {ActivitiesComponent} from "../../../../shared/components/activities/activities.component";
import {ProductionStatusBadgeComponent} from "../../../../shared/components/production-status/production-status-badge";
import {AuthUtils} from "../../../../core/auth/auth.utils";
import {MatOption, MatSelect} from "@angular/material/select";
import {
    MatDatepickerModule,
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatStartDate
} from "@angular/material/datepicker";
import {HolidaysService} from "../../holidays-management/holidays.service";
import {Holiday} from "../../holidays-management/holidays.types";
import {ProductionStatus} from "../../../mng/production-statuses/production-statuses.types";
import {ActivitiesService} from "../../../../shared/components/activities/activities.service";

@Component({
    selector: 'app-absence-leave-details-component',
    templateUrl: './details.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        MatButton,
        TranslocoPipe,
        MatIcon,
        NgIf,
        MatFormField,
        MatLabel,
        MatError,
        ReactiveFormsModule,
        MatInput,
        RouterLink,
        MatProgressBar,
        MatTooltip,
        DatePipe,
        ActivitiesComponent,
        ProductionStatusBadgeComponent,
        MatSelect,
        NgForOf,
        MatOption,
        MatDateRangeInput,
        MatStartDate,
        MatHint,
        MatDatepickerModule,
        MatDatepickerToggle,
        MatDateRangePicker,
        LowerCasePipe,
        NgClass,
        MatSuffix
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbsenceLeaveDetailsComponent implements OnInit, OnDestroy {
    absence: Absence = null;
    editMode: boolean = false;
    progressSubscription: Subscription;

    progress: boolean = false;
    silentProgress: boolean = false;

    actionType: string;

    form: FormGroup;

    private _tagsPanelOverlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    holidaysDates: any[] = [];
    absenceDates: any[] = [];

    // --- NEW: unified blocking for "unselectable" days (existing absences + free holidays) ---
    private blockedTimestamps = new Set<number>(); // start-of-day timestamps (local)
    private blockedSorted: number[] = [];          // sorted list for fast "next blocked day" lookup

    absenceDatesFilter: any;
    maxDate: Date = null;

    /**
     * Constructor
     */
    constructor(
        private toastr: ToastrService,
        private _activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private _absencesLeaveManagement: YearlyPlansPageComponent,
        public _absencesLeaveService: AbsencesLeaveService,
        public activitiesService: ActivitiesService,
        public _holidaysService: HolidaysService,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        public utils: Utils,
        public transloco: TranslocoService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        console.log("Route ", this._activatedRoute)

        // Retrieve id of the absence to be edited
        let id = this._activatedRoute.snapshot.paramMap.get('id');

        if (!id) {
            this._router.navigate(['/hr/leave-management']);
            return;
        }

        // Open the drawer
        this._absencesLeaveManagement.matDrawer.open();

        this._absencesLeaveService.absence$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async absence => {
                this.progress = true;
                if (absence) {
                    // Set the absence object
                    this.absence = absence;

                    // We should retrieve all user related existing absence periods to be used in date range picker
                    try {
                        let resp = await firstValueFrom(
                            this._absencesLeaveService.getUserAbsencesDatePeriods(this.absence.user_id)
                        )

                        const holidaysResp = await firstValueFrom(
                            this._holidaysService.getHolidays(
                                /* page   */ 0,
                                /* size   */ 0,
                                /* sort   */ 'date',
                                /* order  */ 'asc',
                                /* search */ '',
                                /* filter */ {
                                    use_pagination: 0
                                    // country_id: this.countryIdForHolidays
                                }
                            )
                        );

                        this.holidaysDates = holidaysResp?.holidays ?? [];
                        this.absenceDates = resp?.data ?? [];

// Exclude the currently edited absence when in "edit" mode
                        this.absenceDates = this.absenceDates.filter(el => el.id !== this.absence?.id);

// Expand each absence period into individual calendar days
                        let allAbsenceDays: Date[] = [];
                        for (const el of this.absenceDates) {
                            const days = this.getDatesBetweenInclusive(el.from_date, el.to_date);
                            allAbsenceDays.push(...days);
                        }

// Deduplicate and sort (normalize to start-of-day to avoid duplicates)
                        const seen = new Set<number>();
                        allAbsenceDays = allAbsenceDays
                            .map(d => this.startOfDay(d))
                            .filter(d => {
                                const ts = d.getTime();
                                if (seen.has(ts)) return false;
                                seen.add(ts);
                                return true;
                            })
                            .sort((a, b) => a.getTime() - b.getTime());

// Build the "blocked" calendar: previous absences + free holidays,
// but unblock the current absence range if editing.
                        this.rebuildBlockedDates(allAbsenceDays, this.holidaysDates as Holiday[], this.absence);

// Trigger change detection to refresh UI (dateFilter, etc.)
                        this._changeDetectorRef.markForCheck();

                    } catch (e) {
                        console.error(e)
                    }

                    console.log("Absence: ", this.absence)

                    // Create the absence form
                    this.form = this._formBuilder.group({
                        id: [0],
                        from_date: ['', [Validators.required]],
                        to_date: ['', [Validators.required]],
                        user_id: [null, [Validators.required]],
                        additional: ['', []],
                        type: [null, [Validators.required]],
                        status: [this.utils.ABSENCE_STATUS_APPROVED, []]
                    });

                    if (id === 'new' && absence.id === 'new') {
                        this.actionType = "new";
                    } else {
                        this.actionType = "edit"
                    }

                    // Open the drawer in case it is closed
                    this._absencesLeaveManagement.matDrawer.open();

                    // Patch values to the form
                    this.form.patchValue(absence);

                    // this.form.valueChanges.subscribe(val => {
                    //     this._absencesLeaveManagement.edittingItem = true;
                    // })

                    // Toggle the edit mode off
                    if (this.actionType == "new") {
                        this.toggleEditMode(true)
                    } else {
                        this.toggleEditMode(false);
                    }

                    // Mark for check
                    this._changeDetectorRef.markForCheck();

                    // Initiate the fork array
                    let forkArray = [
                        // this._absencesLeaveService.getAbsenceById(
                        //     this._activatedRoute.snapshot.paramMap.get('contact_id'),
                        //     +this._activatedRoute.parent.parent.parent.parent.snapshot.paramMap.get('id')
                        //     // +this.user?.id
                        // )
                    ]


                    forkJoin(forkArray)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((data: any) => {
                            // Set the received data

                            // Mark for check
                            this._changeDetectorRef.detectChanges();
                            this._changeDetectorRef.markForCheck();

                            this.progress = false;

                        }, error => {
                            this.progress = false;
                        })

                } else {
                    // this.isLoading = false;

                    if (id && absence?.id === 'new') {
                        // ignore it
                    } else {

                        try {
                            // we should retrieve the absence by id
                            // Set the absence
                            this._absencesLeaveService.absence =
                                await firstValueFrom(
                                    this._absencesLeaveService.getAbsenceById(
                                        +id
                                    )
                                );
                        } catch (e) {
                            console.error(e)
                            this.progress = false;
                            // lets navigate away
                            this._router.navigate(['/hr/leave-management']);
                        } finally {
                        }

                    }

                }

            })

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(1);
        this._unsubscribeAll.complete();

        this._absencesLeaveService.absence = null;

        // Dispose the overlays if they are still on the DOM
        if (this._tagsPanelOverlayRef) {
            this._tagsPanelOverlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /** Normalize any Date/string to local start-of-day (00:00) */
    private startOfDay(d: Date | string): Date {
        const src = d instanceof Date ? d : new Date(d);
        return new Date(src.getFullYear(), src.getMonth(), src.getDate());
    }

    /** Create a new Date offset by N days (can be negative) */
    private addDays(d: Date, days: number): Date {
        const nd = new Date(d);
        nd.setDate(nd.getDate() + days);
        return nd;
    }

    /** Inclusive list of dates between start..end, each normalized to start-of-day */
    private getDatesBetweenInclusive(start: Date | string, end: Date | string): Date[] {
        const s = this.startOfDay(start);
        const e = this.startOfDay(end);
        const out: Date[] = [];
        for (let dt = new Date(s); dt.getTime() <= e.getTime(); dt.setDate(dt.getDate() + 1)) {
            out.push(new Date(dt));
        }
        return out;
    }

    /**
     * Rebuild the set of "blocked" days:
     * - all days covered by other absences for the same user
     * - all holidays with day_free = 1 (free/non-working)
     * Then, if we're editing an existing absence, we *unblock* its current range
     * so the user can adjust it.
     *
     * Also sets `absenceDatesFilter` (true => selectable).
     */
    private rebuildBlockedDates(absenceDays: Date[], holidays: Holiday[], currentAbsence?: Absence) {
        this.blockedTimestamps.clear();

        // 1) Add all days from other absences
        for (const d of absenceDays) {
            this.blockedTimestamps.add(this.startOfDay(d).getTime());
        }

        // 2) Add all holiday days where day_free is truthy (1/true)
        if (Array.isArray(holidays)) {
            for (const h of holidays) {
                if (h && (h.day_free === 1 || h.day_free === true)) {
                    this.blockedTimestamps.add(this.startOfDay(h.date).getTime());
                }
            }
        }

        // 3) If editing, unblock the currently selected absence range (so it can be changed)
        if (currentAbsence?.from_date && currentAbsence?.to_date) {
            const currSpan = this.getDatesBetweenInclusive(currentAbsence.from_date as any, currentAbsence.to_date as any);
            for (const d of currSpan) {
                this.blockedTimestamps.delete(this.startOfDay(d).getTime());
            }
        }

        // Prepare a sorted array for binary searching the "next blocked day"
        this.blockedSorted = Array.from(this.blockedTimestamps).sort((a, b) => a - b);

        // dateFilter used by <mat-date-range-input>: return true to allow selection
        this.absenceDatesFilter = (d: Date): boolean => {
            if (!d) return true;
            return !this.blockedTimestamps.has(this.startOfDay(d).getTime());
        };
    }

    /**
     * Find the first blocked day *after* the given date.
     * Returns a Date (normalized to start-of-day) or null if none exists.
     * Uses binary search over `blockedSorted`.
     */
    private findNextBlockedAfter(date: Date): Date | null {
        const target = this.startOfDay(date).getTime();
        let lo = 0, hi = this.blockedSorted.length - 1, ans: number | null = null;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (this.blockedSorted[mid] > target) {
                ans = this.blockedSorted[mid];
                hi = mid - 1;
            } else {
                lo = mid + 1;
            }
        }
        return ans !== null ? new Date(ans) : null;
    }

    /**
     * Method for returning complete list of all dates between two dates
     *
     * @param start
     * @param end
     */
    getDaysArray = function (start, end) {
        for (var arr = [], dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
            arr.push(new Date(dt));
        }
        return arr;
    };

    /**
     * Method which notifies user of changes made to object in
     * case of user's request for leaving the component with unsaved changes
     *
     */
    public canDeactivate() {
        this.progress = false;
        return new Promise<any>((resolve, reject) => {
            if (this.form?.dirty) {

                // Open the confirmation dialog
                const confirmation = this._fuseConfirmationService.open({
                    title: this.transloco.translate('ALERTS.CHECK_BEFORE_PROCEED'),
                    message: this.transloco.translate('ALERTS.CHANGES_WILL_BE_LOST'),
                    actions: {
                        cancel: {
                            label: this.transloco.translate('CANCEL')
                        },
                        confirm: {
                            label: this.transloco.translate('PROCEED')
                        }
                    }
                });

                // Subscribe to the confirmation dialog closed action
                confirmation.afterClosed().subscribe((result) => {

                    // If the confirm button pressed...
                    if (result === 'confirmed') {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                });

            } else {
                resolve(true);
            }
        });
    }

    /**
     * Close the drawer
     */
    closeDrawer(): Promise<MatDrawerToggleResult> {
        this._absencesLeaveManagement.edittingItem = false;
        return this._absencesLeaveManagement.matDrawer.close();
    }

    /**
     * Toggle edit mode
     *
     * @param editMode
     */
    toggleEditMode(editMode: boolean | null = null): void {
        this.progress = true;
        if (editMode === null) {
            this.editMode = !this.editMode;
        } else {
            this.editMode = editMode;
        }

        // if user cancel the changes made to the form, patch previous values to form
        if (this.editMode) {
            this._absencesLeaveManagement.edittingItem = true;
        } else {
            // Patch values to the form
            this.form.patchValue(this.absence);

            setTimeout(() => {
                this.markFormAsPristine();
            }, 100)

            this._absencesLeaveManagement.edittingItem = false;

        }

        this.progress = false;

        console.log("Form: ", this.form)

        // Mark for check
        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Method for marking specific form as pristine
     *
     * @private
     */
    private markFormAsPristine() {
        this.form.markAsPristine()
        setTimeout(() => {
            // Mark for check
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
        }, 200)
    }


    /**
     * Method for canceling editing screen
     *
     */
    cancel() {
        if (this.form.dirty || this.actionType === 'new') {

            // Open the confirmation dialog
            const confirmation = this._fuseConfirmationService.open({
                title: this.transloco.translate('ALERTS.CHECK_BEFORE_PROCEED'),
                message: this.transloco.translate('ALERTS.CHANGES_WILL_BE_LOST'),
                actions: {
                    cancel: {
                        label: this.transloco.translate('CANCEL')
                    },
                    confirm: {
                        label: this.transloco.translate('PROCEED')
                    }
                }
            });

            // Subscribe to the confirmation dialog closed action
            confirmation.afterClosed().subscribe((result) => {

                // If the confirm button pressed...
                if (result === 'confirmed') {

                    if (this.actionType == "new") {
                        this._router.navigate(['../'], {relativeTo: this._activatedRoute});
                        // Delete the absence
                        // this._absencesLeaveService.deleteAbsence(this.absence.id)
                        //     .pipe(takeUntil(this._unsubscribeAll))
                        //     .subscribe((isDeleted) => {
                        //         this._router.navigate(['../'], {relativeTo: this._activatedRoute});
                        //     });
                    }

                    this.toggleEditMode(false)

                }
            })

        } else {

            if (this.actionType == "new") {
                this._router.navigate(['../'], {relativeTo: this._activatedRoute});
                // // Delete the absence
                // this._absencesLeaveService.deleteAbsence(this.absence.id)
                //     .pipe(takeUntil(this._unsubscribeAll))
                //     .subscribe((isDeleted) => {
                //         this._router.navigate(['../'], {relativeTo: this._activatedRoute});
                //     });
            }

            this.toggleEditMode(false)

        }

    }

    /**
     * Update the absence
     */
    updateAbsence(): void {
        this.progress = true;

        // detect changes
        this._changeDetectorRef.detectChanges();

        // Get the absence object
        const absence = this.form.getRawValue();

        console.log(absence);

        // if action type is new insert new object in db
        if (this.actionType === 'new') {
            // Add new absence in db
            this._absencesLeaveService.addAbsence(absence)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(async (response) => {

                    // display toastr success message
                    this.toastr.success('', this.transloco.translate('SUCCESS'));

                    // Update the absence object from service
                    this._absencesLeaveService.absence = response.data;

                    this._absencesLeaveManagement.refreshYear();

                    // this.customer_contacts.push(response)
                    // Toggle the edit mode off
                    this.toggleEditMode(false);
                    this.progress = false;

                }, error => {
                    this.progress = false;
                    throw error;
                });

        } else { // otherwise update
            // Update the absence with specific id in db
            this._absencesLeaveService.updateAbsence(absence)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(async (response) => {

                    // display toastr success message
                    this.toastr.success('', this.transloco.translate('SUCCESS'));

                    // Update the absence object from service
                    this._absencesLeaveService.absence = response.data;

                    this._absencesLeaveManagement.refreshYear();

                    // Toggle the edit mode off
                    this.toggleEditMode(false);
                    this.progress = false;

                }, error => {
                    this.progress = false;
                    throw error;
                });

        }

    }

    /**
     * Delete the absence
     */
    deleteAbsence(): void {
        // We should check if we are in new mode
        if (this.actionType === 'new') {
            // we should just navigate away
            this.cancel();
            return;
        }

        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open();

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {

            // If the confirm button pressed...
            if (result === 'confirmed') {

                this.progress = true;

                // detect changes
                this._changeDetectorRef.detectChanges();

                // Delete the absence
                this._absencesLeaveService.deleteAbsence(this.absence.id)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(async () => {

                        // display toastr success message
                        this.toastr.success('', this.transloco.translate('SUCCESS'));

                        this._absencesLeaveManagement.refreshYear();
                        this.markFormAsPristine();
                        this._router.navigate(['../'], {relativeTo: this._activatedRoute});

                        // Toggle the edit mode off
                        this.toggleEditMode(false);
                        this.progress = false;
                    }, err => {
                        this.progress = false;
                        console.error(err)
                    });

                // Mark for check
                this._changeDetectorRef.markForCheck();


            }
        });

    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * When the "from" date changes, set the maximum allowed "to" date.
     * We set max to the day BEFORE the next blocked date (absence/holiday).
     * If there is no next blocked day, we remove the max and allow any later date.
     */
    fromDatetimeChanged(event: any) {
        const value: Date | null = event?.value ? this.startOfDay(event.value) : null;

        // No from-date => remove limit
        if (!value) {
            this.maxDate = null;
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
            return;
        }

        // Find the first blocked day strictly after the chosen "from" date
        const nextBlocked = this.findNextBlockedAfter(value);

        if (nextBlocked) {
            // Restrict the range to end the day before the next blocked day
            const candidate = this.addDays(nextBlocked, -1);
            // Safety: ensure maxDate never ends up before from-date
            this.maxDate = candidate.getTime() < value.getTime() ? new Date(value) : candidate;
        } else {
            // No conflicts ahead -> no upper bound
            this.maxDate = null;
        }

        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Method which resets the max date and clears range
     *
     */
    clearDateRange() {
        this.maxDate = null;
        this.form.get('from_date').reset();
        this.form.get('to_date').reset();
        this.form.get('from_date').updateValueAndValidity();
        this.form.get('to_date').updateValueAndValidity();
        this.form.updateValueAndValidity();
    }

    protected readonly AuthUtils = AuthUtils;

    /**
     * Method for handling action click
     *
     * @param status
     */
    onStatusChanged(status: ProductionStatus): void {
        // Emit the status change only if the current status is different
        if (this.absence.status === status.status_id) {
            return;
        }
        const tmp = this.absence.status;

        // We do need to display a prompt to the user to confirm the change of the work order status
        const confirmation = this._fuseConfirmationService.open({
            title: this.transloco.translate('ABSENCES.CHANGE_STATUS_PROMPT.HEADER'),
            message: this.transloco.translate('ABSENCES.CHANGE_STATUS_PROMPT.BODY', {status: status.name}),
            actions: {
                cancel: {
                    label: this.transloco.translate('CANCEL')
                },
                confirm: {
                    label: this.transloco.translate('CONFIRM')
                }
            }
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {

            // If the confirm button pressed...
            if (result === 'confirmed') {

                this.silentProgress = true;

                // Detect changes
                this._changeDetectorRef.markForCheck();
                this._changeDetectorRef.detectChanges();

                this.absence.status = status.status_id;

                this._absencesLeaveService.changeAbsenceStatus(this.absence)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(
                        () => {

                            this.toastr.success(
                                '',
                                this.transloco.translate('ALERTS.UPDATE_SUCCESSFUL')
                            );

                            this._changeDetectorRef.markForCheck();
                            this._changeDetectorRef.detectChanges();

                            this._absencesLeaveService.absence = this.absence;

                            this._absencesLeaveManagement.refreshYear();

                            this.silentProgress = false;

                        },
                        (error) => {
                            this.absence.status = tmp;
                            this.silentProgress = false;
                            this._changeDetectorRef.markForCheck();
                            this._changeDetectorRef.detectChanges();
                        }
                    );

            } else {
                console.log("Cancelled", tmp, this.absence.status);

                // ignore it
                this.absence.status = tmp;
                this.silentProgress = false;
                this._changeDetectorRef.markForCheck();
                this._changeDetectorRef.detectChanges();
            }
        });

    }

}
